import { useEffect } from "react"
import useSWR, { mutate } from "swr"
import useSWRMutation from "swr/mutation"
import { v4 as randomUUID } from "uuid"
import { useAppSelector } from "integrations/redux/hooks"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { selectActivatedFireId } from "features/map/control/selectors"
import { selectUserID } from "user/profile/selectors"
import { fetchApiGateway } from "@nova/fetch-api-gateway"
import { Comment, CommentContextMap, CommentTarget } from "@nova/schemas"

const COMMENTS_KEY_ROOT = "comments"

const threadKey = (orgId: string, projectId: string, entityId: string) => [
    COMMENTS_KEY_ROOT,
    orgId,
    projectId,
    entityId,
]

const projectMapKey = (orgId: string, projectId: string) => [
    COMMENTS_KEY_ROOT,
    "project",
    orgId,
    projectId,
]

const isCommentsKey = (key: unknown): key is unknown[] =>
    Array.isArray(key) && key[0] === COMMENTS_KEY_ROOT

const isMapTarget = (target: CommentTarget) => target === "map-pin" || target === "map-comment"

const invalidateComments = () => mutate(isCommentsKey)

const invalidateThread = (orgId: string, projectId: string, entityId: string) => {
    void mutate(threadKey(orgId, projectId, entityId))
    void mutate(projectMapKey(orgId, projectId))
}

const editComments = (
    key: Parameters<typeof mutate>[0],
    edit: (comments: Comment[]) => Comment[]
) =>
    mutate(key, (current: Comment[] | undefined) => current && edit(current), { revalidate: false })

const dedupeById = (comments: Comment[]) => {
    const byId = new Map(comments.map((comment) => [comment.id, comment]))
    return comments.length === byId.size ? comments : [...byId.values()]
}

const writeToCaches = (
    orgId: string,
    projectId: string,
    entityId: string,
    target: CommentTarget,
    edit: (comments: Comment[]) => Comment[]
) => {
    const apply = (current: Comment[] | undefined) => current && dedupeById(edit(current))
    if (isMapTarget(target)) {
        void mutate(projectMapKey(orgId, projectId), apply, { revalidate: false })
    }
    void mutate(threadKey(orgId, projectId, entityId), apply, { revalidate: false })
}

const addReactionToComment = (comment: Comment, emoji: string, userId: string) => {
    const existing = comment.reactions.find((reaction) => reaction.emoji === emoji)
    if (!existing) {
        return { ...comment, reactions: [...comment.reactions, { emoji, userIds: [userId] }] }
    }
    if (existing.userIds.includes(userId)) return comment
    return {
        ...comment,
        reactions: comment.reactions.map((reaction) =>
            reaction.emoji === emoji
                ? { ...reaction, userIds: [...reaction.userIds, userId] }
                : reaction
        ),
    }
}

const removeReactionFromComment = (comment: Comment, emoji: string, userId: string) => {
    const reactions = comment.reactions.flatMap((reaction) => {
        if (reaction.emoji !== emoji) return [reaction]
        const userIds = reaction.userIds.filter((id) => id !== userId)
        return userIds.length > 0 ? [{ ...reaction, userIds }] : []
    })
    return { ...comment, reactions }
}

const patchCachedComment = (commentId: string, patch: (comment: Comment) => Comment) =>
    editComments(isCommentsKey, (comments) =>
        comments.map((comment) => (comment.id === commentId ? patch(comment) : comment))
    )

const optimisticCommentId = () => `optimistic-${randomUUID()}`

type CommentDraft = {
    id: string
    entityId: string
    comment: string
    context: CommentContextMap[CommentTarget]
    createdBy: string
    imageKeys: string[]
}

const seedComment = (orgId: string, projectId: string, draft: CommentDraft) => {
    const optimistic: Comment = {
        ...draft,
        ownerId: projectId,
        mentionedUserIds: [],
        numReplies: 0,
        isUnread: false,
        participantIds: [],
        reactions: [],
        createdAt: Date.now(),
    }
    const hasRoot = (comments: Comment[]) =>
        draft.context.target === "map-pin" &&
        comments.some(
            (comment) => comment.entityId === draft.entityId && comment.context.target === "map-pin"
        )
    writeToCaches(orgId, projectId, draft.entityId, draft.context.target, (comments) =>
        hasRoot(comments) ? comments : [...comments, optimistic]
    )
}

const replaceOptimisticComment = (
    orgId: string,
    projectId: string,
    draft: CommentDraft,
    created: Comment
) =>
    writeToCaches(orgId, projectId, draft.entityId, draft.context.target, (comments) => [
        ...comments.filter((comment) => comment.id !== draft.id),
        created,
    ])

const unseedComment = (orgId: string, projectId: string, draft: CommentDraft) =>
    writeToCaches(orgId, projectId, draft.entityId, draft.context.target, (comments) =>
        comments.filter((comment) => comment.id !== draft.id)
    )

const byCreatedAt = <T extends CommentTarget>(comments: Comment<T>[] | undefined) =>
    comments && [...comments].sort((a, b) => a.createdAt - b.createdAt || a.id.localeCompare(b.id))

const useComments = <T extends CommentTarget>(params: { entityId: string }) => {
    const workspaceId = useAppSelector(selectActiveWorkspaceId()) ?? ""
    const projectId = useAppSelector(selectActivatedFireId) ?? ""
    const { data, isLoading } = useSWR(
        workspaceId && projectId && params.entityId
            ? threadKey(workspaceId, projectId, params.entityId)
            : null,
        () =>
            fetchApiGateway("get /org/:orgId/project/:projectId/entityId/:entityId/comment", {
                params: { orgId: workspaceId, projectId, entityId: params.entityId },
            }) as Promise<Comment<T>[]>
    )

    return { comments: byCreatedAt(data), isLoading }
}

const useProjectMapComments = () => {
    const workspaceId = useAppSelector(selectActiveWorkspaceId()) ?? ""
    const projectId = useAppSelector(selectActivatedFireId) ?? ""
    const { data, isLoading } = useSWR(
        workspaceId && projectId ? projectMapKey(workspaceId, projectId) : null,
        () =>
            fetchApiGateway("get /org/:orgId/project/:projectId/comment", {
                params: { orgId: workspaceId, projectId },
            }) as Promise<Comment<"map-pin" | "map-comment">[]>
    )

    return { comments: byCreatedAt(data), isLoading }
}

const usePrefetchMapCommentThreads = () => {
    const workspaceId = useAppSelector(selectActiveWorkspaceId()) ?? ""
    const projectId = useAppSelector(selectActivatedFireId) ?? ""
    const { comments } = useProjectMapComments()

    useEffect(() => {
        if (!comments || !workspaceId || !projectId) return

        const byEntityId = new Map<string, Comment<"map-pin" | "map-comment">[]>()
        for (const comment of comments) {
            const thread = byEntityId.get(comment.entityId)
            if (thread) thread.push(comment)
            else byEntityId.set(comment.entityId, [comment])
        }

        for (const [entityId, thread] of byEntityId) {
            mutate(threadKey(workspaceId, projectId, entityId), thread, { revalidate: false })
        }
    }, [comments, workspaceId, projectId])
}

const useCreateComment = () => {
    const orgId = useAppSelector(selectActiveWorkspaceId()) ?? ""
    const projectId = useAppSelector(selectActivatedFireId) ?? ""
    const meUserId = useAppSelector(selectUserID) ?? ""
    const { trigger: createComment, isMutating } = useSWRMutation(
        "create-comment",
        async (
            key,
            {
                arg,
            }: {
                arg: {
                    comment: string
                    context: CommentContextMap[keyof CommentContextMap]
                    entityId: string
                    imageFileIds?: string[]
                    position?: { longitude: number; latitude: number }
                }
            }
        ) => {
            const draft = {
                id: optimisticCommentId(),
                entityId: arg.entityId,
                comment: arg.comment,
                context: arg.context,
                createdBy: meUserId,
                imageKeys: [],
            }
            seedComment(orgId, projectId, draft)

            try {
                const created = await fetchApiGateway(
                    "post /org/:orgId/project/:projectId/entityId/:entityId/comment",
                    {
                        params: { orgId, projectId, entityId: arg.entityId },
                        body: arg,
                    }
                )

                replaceOptimisticComment(orgId, projectId, draft, created)

                return created
            } catch (error) {
                unseedComment(orgId, projectId, draft)
                throw error
            }
        }
    )

    return { createComment, isMutating }
}

const useDeleteComment = () => {
    const orgId = useAppSelector(selectActiveWorkspaceId()) ?? ""
    const projectId = useAppSelector(selectActivatedFireId) ?? ""

    const { trigger: deleteComment, isMutating } = useSWRMutation(
        "delete-comment",
        async (key, { arg: { commentId } }: { arg: { commentId: string } }) =>
            fetchApiGateway("delete /org/:orgId/project/:projectId/comment/:commentId", {
                params: { orgId, projectId, commentId },
            }),
        {
            onSuccess: invalidateComments,
        }
    )

    return { deleteComment, isMutating }
}

const useUpdateComment = () => {
    const orgId = useAppSelector(selectActiveWorkspaceId()) ?? ""
    const projectId = useAppSelector(selectActivatedFireId) ?? ""

    const { trigger: updateComment, isMutating } = useSWRMutation(
        "update-comment",
        async (
            key,
            {
                arg: updates,
            }: {
                arg: {
                    commentId: string
                    comment?: string
                    context?: CommentContextMap[CommentTarget]
                    imageFileIds?: string[]
                }
            }
        ) =>
            fetchApiGateway("patch /org/:orgId/project/:projectId/comment/:commentId", {
                params: { orgId, projectId, commentId: updates.commentId },
                body: updates,
            }),
        {
            onSuccess: (data) => invalidateThread(orgId, projectId, data.entityId),
        }
    )

    return { updateComment, isMutating }
}

const useAddReaction = () => {
    const orgId = useAppSelector(selectActiveWorkspaceId()) ?? ""
    const projectId = useAppSelector(selectActivatedFireId) ?? ""
    const meUserId = useAppSelector(selectUserID) ?? ""

    const { trigger: addReaction, isMutating } = useSWRMutation(
        "add-reaction",
        async (key, { arg }: { arg: { commentId: string; emoji: string } }) => {
            patchCachedComment(arg.commentId, (comment) =>
                addReactionToComment(comment, arg.emoji, meUserId)
            )
            try {
                return await fetchApiGateway(
                    "post /org/:orgId/project/:projectId/comment/:commentId/reaction",
                    {
                        params: { orgId, projectId, commentId: arg.commentId },
                        body: { emoji: arg.emoji },
                    }
                )
            } catch (error) {
                invalidateComments()
                throw error
            }
        }
    )

    return { addReaction, isMutating }
}

const useRemoveReaction = () => {
    const orgId = useAppSelector(selectActiveWorkspaceId()) ?? ""
    const projectId = useAppSelector(selectActivatedFireId) ?? ""
    const meUserId = useAppSelector(selectUserID) ?? ""

    const { trigger: removeReaction, isMutating } = useSWRMutation(
        "remove-reaction",
        async (key, { arg }: { arg: { commentId: string; emoji: string } }) => {
            patchCachedComment(arg.commentId, (comment) =>
                removeReactionFromComment(comment, arg.emoji, meUserId)
            )
            try {
                return await fetchApiGateway(
                    "delete /org/:orgId/project/:projectId/comment/:commentId/reaction",
                    {
                        params: { orgId, projectId, commentId: arg.commentId },
                        body: { emoji: arg.emoji },
                    }
                )
            } catch (error) {
                invalidateComments()
                throw error
            }
        }
    )

    return { removeReaction, isMutating }
}

const useMarkThreadRead = () => {
    const workspaceId = useAppSelector(selectActiveWorkspaceId()) ?? ""
    const projectId = useAppSelector(selectActivatedFireId) ?? ""

    const { trigger: markThreadRead } = useSWRMutation(
        "mark-thread-read",
        async (key, { arg }: { arg: { rootCommentId: string } }) => {
            patchCachedComment(arg.rootCommentId, (comment) => ({ ...comment, isUnread: false }))
            return await fetchApiGateway(
                "post /org/:orgId/project/:projectId/comment/:commentId/read",
                { params: { orgId: workspaceId, projectId, commentId: arg.rootCommentId } }
            )
        }
    )

    return { markThreadRead }
}

export {
    useComments,
    useMarkThreadRead,
    useProjectMapComments,
    usePrefetchMapCommentThreads,
    useCreateComment,
    useDeleteComment,
    useUpdateComment,
    useAddReaction,
    useRemoveReaction,
}
